import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DashBoardActions } from "../../store/ducks/dashboard";
import { AppActions } from "../../store/ducks/app";
import {
	OperationalDashboardActions,
	TodayFocusItem,
	TodayFocusQueueKey,
} from "../../store/ducks/operationalDashboard";
import { CopilotPatientReference } from "./services/clinicalDashboardService";
import { CopilotSummarySnapshot } from "./components/AICopilotSection/useAICopilotViewModel";
import { todayFocusItemIdentity } from "./services/todayFocusService";

interface TodayFocusContactTarget {
	queueKey: TodayFocusQueueKey;
	item: TodayFocusItem;
}

export interface MetricData {
	id: string;
	title: string;
	value: number;
	unit: string;
	accentColor: string;
}

export const useOperationalDashboardViewModel = () => {
	const dispatch = useDispatch();
	const [isReevaluationModalOpen, setIsReevaluationModalOpen] = useState(false);
	const [isCriticalAlertModalOpen, setIsCriticalAlertModalOpen] =
		useState(false);
	const [isRecentEvaluationsModalOpen, setIsRecentEvaluationsModalOpen] =
		useState(false);
	const [isPatientListModalOpen, setIsPatientListModalOpen] = useState(false);
	const [isCopilotPatientModalOpen, setIsCopilotPatientModalOpen] =
		useState(false);
	const [isDetailedRecordModalOpen, setIsDetailedRecordModalOpen] =
		useState(false);
	const [copilotPatient, setCopilotPatient] =
		useState<CopilotPatientReference | null>(null);
	const [copilotSummarySnapshot, setCopilotSummarySnapshot] =
		useState<CopilotSummarySnapshot | null>(null);
	const [todayFocusContactTarget, setTodayFocusContactTarget] =
		useState<TodayFocusContactTarget | null>(null);

	const totalPatients = useSelector(
		(state: any) => state.dashboard.total_pacientes
	);
	const reduxPatients = useSelector((state: any) => state.dashboard.pacientes);
	const ultimaAvaliacaoFinalizada = useSelector(
		(state: any) => state.operationalDashboard.ultimaAvaliacaoFinalizada
	);

	// Select dynamic indicators lists from the Redux store
	const prontosReavaliacao = useSelector(
		(state: any) => state.operationalDashboard.prontosReavaliacao
	);
	const pacientesInativos = useSelector(
		(state: any) => state.operationalDashboard.pacientesInativos
	);
	const pacientesAvaliados = useSelector(
		(state: any) => state.operationalDashboard.pacientesAvaliados
	);
	const isLoadingResumo = useSelector(
		(state: any) => !!state.operationalDashboard.loadingResumo
	);
	const resumoError = useSelector(
		(state: any) => state.operationalDashboard.resumoError || null
	);
	const hasLoadedResumo = useSelector(
		(state: any) => !!state.operationalDashboard.hasLoadedResumo
	);
	const todayFocus = useSelector(
		(state: any) => state.operationalDashboard.todayFocus
	);

	// Request dashboard summary counts and lists on mount
	useEffect(() => {
		dispatch?.(OperationalDashboardActions?.operational_resumo_request?.());
		dispatch?.(OperationalDashboardActions?.agenda_interest_verify_request?.());
	}, [dispatch]);

	// A fonte inicial do Copiloto é a última avaliação finalizada do resumo.
	useEffect(() => {
		if (ultimaAvaliacaoFinalizada?.paciente && !copilotPatient) {
			setCopilotPatient({
				id: ultimaAvaliacaoFinalizada.paciente.id,
				nome: ultimaAvaliacaoFinalizada.paciente.nome,
				foto: ultimaAvaliacaoFinalizada.paciente.foto || null,
				realizadaEm: ultimaAvaliacaoFinalizada.realizadaEm,
			});
		}
	}, [ultimaAvaliacaoFinalizada, copilotPatient]);

	const navigateHome = () => {
		dispatch(DashBoardActions.set_current_page("pacientes"));
	};

	const handleOpenPatientListModal = () => {
		const hasNoPatients =
			(!reduxPatients || reduxPatients.length === 0) && totalPatients === 0;
		if (hasNoPatients) {
			dispatch(AppActions.set_modal_adicionar_paciente(true));
		} else {
			setIsPatientListModalOpen(true);
		}
	};

	const metrics: MetricData[] = [
		{
			id: "reavaliacao",
			title: "Prontos para reavaliação",
			value: prontosReavaliacao?.total || 0,
			unit: "Pacientes",
			accentColor: "#00D084",
		},
		{
			id: "inativos",
			title: "Clientes inativos",
			value: pacientesInativos?.total || 0,
			unit: "Pacientes",
			accentColor: "#ff5f57",
		},
		{
			id: "avaliados",
			title: "Avaliados nos últimos 30 dias",
			value: pacientesAvaliados?.total || 0,
			unit: "Atendimentos",
			accentColor: "#010E4B",
		},
	];

	const handleMetricClick = (id: string) => {
		if (id === "reavaliacao") {
			setIsReevaluationModalOpen(true);
		} else if (id === "inativos") {
			setIsCriticalAlertModalOpen(true);
		} else if (id === "avaliados") {
			setIsRecentEvaluationsModalOpen(true);
		}
	};

	const handleOpenTodayFocusContact = (
		queueKey: TodayFocusQueueKey,
		item: TodayFocusItem
	) => {
		setTodayFocusContactTarget({ queueKey, item });
		if (queueKey === "convidarReavaliacao") {
			setIsReevaluationModalOpen(true);
		} else if (queueKey === "resgatarCliente") {
			setIsCriticalAlertModalOpen(true);
		}
	};

	const handleTodayFocusContactStarted = (patientId: string) => {
		const target = todayFocusContactTarget;
		if (!target || String(target.item.ID_USUARIO) !== patientId) return;
		const queue = todayFocus?.queues?.[target.queueKey];
		if (
			!queue?.items?.[0] ||
			todayFocusItemIdentity(queue.items[0]) !==
				todayFocusItemIdentity(target.item)
		)
			return;
		dispatch(
			OperationalDashboardActions.today_focus_action_confirmed(
				target.queueKey,
				todayFocusItemIdentity(target.item),
				"success"
			)
		);
		window.setTimeout(() => {
			dispatch(
				OperationalDashboardActions.today_focus_action_success(
					target.queueKey,
					todayFocusItemIdentity(target.item)
				)
			);
			if (queue.items.length === 1 && queue.temProxima) {
				dispatch(OperationalDashboardActions.today_focus_request());
			}
		}, 400);
	};

	const closeReevaluationModal = () => {
		setIsReevaluationModalOpen(false);
		setTodayFocusContactTarget(null);
	};

	const closeCriticalAlertModal = () => {
		setIsCriticalAlertModalOpen(false);
		setTodayFocusContactTarget(null);
	};

	const handleCopilotSummaryStateChange = useCallback(
		(snapshot: CopilotSummarySnapshot) => setCopilotSummarySnapshot(snapshot),
		[]
	);

	const handleRefreshLatestCopilotPatient = useCallback(() => {
		if (!ultimaAvaliacaoFinalizada?.paciente) return null;
		const latestPatient: CopilotPatientReference = {
			id: ultimaAvaliacaoFinalizada.paciente.id,
			nome: ultimaAvaliacaoFinalizada.paciente.nome,
			foto: ultimaAvaliacaoFinalizada.paciente.foto || null,
			realizadaEm: ultimaAvaliacaoFinalizada.realizadaEm,
		};
		setCopilotPatient(latestPatient);
		return latestPatient;
	}, [ultimaAvaliacaoFinalizada]);

	return {
		navigateHome,
		metrics,
		isLoadingResumo: isLoadingResumo && !hasLoadedResumo,
		resumoError: !hasLoadedResumo ? resumoError : null,
		retryResumo: () =>
			dispatch(OperationalDashboardActions.operational_resumo_request()),
		handleMetricClick,
		isReevaluationModalOpen,
		setIsReevaluationModalOpen,
		isCriticalAlertModalOpen,
		setIsCriticalAlertModalOpen,
		isRecentEvaluationsModalOpen,
		setIsRecentEvaluationsModalOpen,
		isPatientListModalOpen,
		setIsPatientListModalOpen,
		handleOpenPatientListModal,
		isCopilotPatientModalOpen,
		setIsCopilotPatientModalOpen,
		copilotPatient,
		setCopilotPatient,
		copilotSummarySnapshot,
		handleCopilotSummaryStateChange,
		handleRefreshLatestCopilotPatient,
		isDetailedRecordModalOpen,
		setIsDetailedRecordModalOpen,
		todayFocusContactTarget,
		handleOpenTodayFocusContact,
		handleTodayFocusContactStarted,
		closeReevaluationModal,
		closeCriticalAlertModal,
	};
};
